<template>
  <div>
    <v-dialog v-model="showWindow" scrollable max-width="450px" persistent>
      <v-card>
        <v-card-actions>
          <h3 class="pl-2">
            <span v-if="id">{{ $t("goods.changing_nomenclature") }}</span>
            <span v-else>{{ $t("goods.creating_nomenclature") }}</span>
          </h3>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="close"
            :disabled="isDisabledForm"
            :loading="isDisabledForm"
          >
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-actions>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-form ref="nomenclatureForm">
            <v-text-field
              outlined
              :disabled="isDisabledForm"
              hide-details="auto"
              class="mt-2"
              ref="formBrandField"
              :rules="[
                (v) => !!v || $t('form.validationErrors.field_is_required'),
              ]"
              v-model="emptyNomenclature.brand"
              :label="$t('common.brand')"
            ></v-text-field>
            <v-text-field
              outlined
              :disabled="isDisabledForm"
              hide-details="auto"
              class="mt-2"
              :rules="[
                (v) => !!v || $t('form.validationErrors.field_is_required'),
              ]"
              v-model="emptyNomenclature.part_code"
              :label="$t('common.part_code')"
            ></v-text-field>
            <v-text-field
              outlined
              :disabled="isDisabledForm"
              hide-details="auto"
              class="mt-2"
              v-model="emptyNomenclature.name"
              :label="$t('common.title')"
            ></v-text-field>
            <v-select
              class="mt-2"
              :label="$t('goods.unit_of_measurements')"
              :items="unitsOfMeasurement"
              v-model="emptyNomenclature.unit_of_measurement"
              item-text="name"
              outlined
              return-object
              :disabled="isDisabledForm || !isFetched"
              :loading="isDisabledForm || !isFetched"
              :rules="[
                (v) => !!v || $t('form.validationErrors.field_is_required'),
              ]"
            >
              <template v-slot:selection="{ item }">
                <span v-if="item && item.name && isFetched">
                  ({{ $t(`goods.units_of_measurements.${item.name}.short`) }})
                </span>
              </template>
              <template v-slot:item="{ item }">
                <span v-if="item && item.name && isFetched">
                  ({{ $t(`goods.units_of_measurements.${item.name}.short`) }})
                  <span
                    class="caption"
                    v-if="item.is_divisible && isEnabledStorageV2"
                  >
                    {{ $t(`goods.is_divisible`) }}
                  </span>
                </span>
              </template>
            </v-select>
            <h4>{{ $t("goods.volume_and_weight_characteristics") }}:</h4>
            <v-text-field
              outlined
              hide-details="auto"
              class="mt-2"
              :disabled="isDisabledForm"
              :rules="[
                (v) =>
                  v === null ||
                  v === '' ||
                  (v &&
                    String(v).replace(/,/g, '.') ==
                      parseFloat(String(v).replace(/,/g, '.'))) ||
                  $t('form.validationErrors.must_be_float'),
              ]"
              v-model="emptyNomenclature.weight"
              :label="$t('goods.weight_in_g')"
            ></v-text-field>
            <v-text-field
              outlined
              hide-details="auto"
              class="mt-2"
              :disabled="isDisabledForm"
              :rules="[
                (v) =>
                  v === null ||
                  v === '' ||
                  (v &&
                    String(v).replace(/,/g, '.') ==
                      parseFloat(String(v).replace(/,/g, '.'))) ||
                  $t('form.validationErrors.must_be_float'),
              ]"
              v-model="emptyNomenclature.depth"
              :label="$t('goods.depth_in_mm')"
            ></v-text-field>
            <v-text-field
              outlined
              hide-details="auto"
              class="mt-2"
              :disabled="isDisabledForm"
              :rules="[
                (v) =>
                  v === null ||
                  v === '' ||
                  (v &&
                    String(v).replace(/,/g, '.') ==
                      parseFloat(String(v).replace(/,/g, '.'))) ||
                  $t('form.validationErrors.must_be_float'),
              ]"
              v-model="emptyNomenclature.width"
              :label="$t('goods.width_in_mm')"
            ></v-text-field>
            <v-text-field
              outlined
              hide-details="auto"
              class="mt-2"
              :disabled="isDisabledForm"
              :rules="[
                (v) =>
                  v === null ||
                  v === '' ||
                  (v &&
                    String(v).replace(/,/g, '.') ==
                      parseFloat(String(v).replace(/,/g, '.'))) ||
                  $t('form.validationErrors.must_be_float'),
              ]"
              v-model="emptyNomenclature.height"
              :label="$t('goods.height_in_mm')"
            ></v-text-field>

            <h4 class="mt-5">{{ $t("common.picture") }}:</h4>
            <v-img
              v-if="emptyNomenclature.image_url && !isDisabledForm && !emptyNomenclature.file"
              class="mb-0"
              :src="emptyNomenclature.image_url"
            ></v-img>
            <span v-if="emptyNomenclature.file">
              <v-icon x-small @click="emptyNomenclature.file=null;">fa-times</v-icon> {{ emptyNomenclature.file.name }}
            </span>
            <v-btn
              v-if="!isDisabledForm && emptyNomenclature.file===null"
              color="success"
              class="elevation-0 d-block mx-auto mt-3"
              @click="$refs['uploadImage'].click()"
              :loading="isImageLoading"
              :disabled="isImageLoading"
            >
              <span v-if="emptyNomenclature.image_url">{{ $t("common.replace") }}</span>
              <span v-else>{{ $t("common.upload") }}</span>
            </v-btn>
            <input
              v-show="false"
              ref="uploadImage"
              :key="inputKey"
              type="file"
              @change="uploadImage"
              accept="image/gif, image/jpeg, image/png"
            />
          </v-form>
        </v-card-text>
        <v-divider class=""></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            color="red"
            :disabled="isDisabledForm"
            :loading="isDisabledForm"
            class="white--text"
            @click="close"
          >
            {{ $t("common.close") }}
          </v-btn>
          <v-btn
            elevation="0"
            color="primary"
            :disabled="isDisabledForm"
            :loading="isDisabledForm"
            @click="submit"
          >
            <span v-if="id">{{ $t("common.save_changes") }}</span>
            <span v-else>{{ $t("common.create") }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapFields } from "vuex-map-fields";

export default {
  name: "NomenclatureForm",
  props: {
    id: null,
    show: {
      type: Boolean,
      default: false,
    },
    action: {
      validator(value) {
        // The value must match one of these strings
        return ["create", "update"].includes(value);
      },
    },
  },
  data: () => ({
    inputKey:0,
    emptyNomenclature: {
      image_url: null,
      brand: null,
      part_code: null,
      name: null,
      unit_of_measurement: null,
      depth: null,
      width: null,
      height: null,
      weight: null,
      file:null
    },
  }),
  mounted() {

  },
  methods: {
    uploadImage() {
      this.emptyNomenclature.file = this.$refs["uploadImage"].files[0];
      this.inputKey++;
    },
    isEnabledStorageV2() {
      if (process.env.VUE_APP_IS_ENABLED_STORAGE_V2) {
        return (
          process.env.VUE_APP_IS_ENABLED_STORAGE_V2 === true ||
          process.env.VUE_APP_IS_ENABLED_STORAGE_V2.toLowerCase() === "true"
        );
      }
      return false;
    },
    close() {
      this.showWindow = false;
    },
    submit() {
      if (!this.$refs.nomenclatureForm.validate()) return;
      if (!this.id && this.action === "create") {
        this.$store
          .dispatch("nomenclatures/store", this.emptyNomenclature)
          .then((created) => {
            if (created.id > 0) {
              let items = [];
              items.push(created);
              this.$emit("store", items);
              this.$emit("hide");
            }
          });
      }
      if (this.id && this.action === "update") {
        this.$confirm( {
          title : this.$t('common.confirm'),
          text : this.$t('goods.confirm_changing_nomenclature'),
          cancelText : this.$t('common.no'),
          acceptText : this.$t('common.yes')
        } ).then( y =>{
            this.$store
              .dispatch("nomenclatures/update", this.emptyNomenclature)
              .then((updated) => {
                if (updated && updated.id > 0) {
                  this.$emit("update", updated);
                  this.$emit("hide");
                }
              });
          })
      }
    },
  },
  computed: {
    isDisabledForm() {
      return (
        this.savingInProgress ||
        (this.nowProcessingItemWithId === this.id &&
          this.nowProcessingItemWithId !== null) ||
        !this.isFetched
      );
    },
    ...mapFields("unit_of_measurements", ["unitsOfMeasurement", "isFetched"]),
    ...mapFields("nomenclatures", [
      "savingInProgress",
      "nomenclature",
      "nowProcessingItemWithId",
      "isImageLoading",
    ]),
    showWindow: {
      get() {
        return this.show;
      },
      set() {
        this.$emit("hide");
      },
    },
  },
  watch: {
    show(to) {
      if (to) {
        this.$store.dispatch("unit_of_measurements/get");
      } else {
        this.emptyNomenclature = {
          brand: null,
          part_code: null,
          name: null,
          unit_of_measurement: null,
          depth: null,
          width: null,
          height: null,
          weight: null,
          file:null
        };
        this.$refs.nomenclatureForm.resetValidation();
      }
      if (this.id) {
        this.$store.dispatch("nomenclatures/getOne", this.id).then((one) => {
          if (one && one.id) {
            this.emptyNomenclature = this.nomenclature;
            this.emptyNomenclature.file = null;
          }
        });
      }
    },
  },
};
</script>

<style scoped></style>
